import type { Table, TableShape } from '@guabi/shared';

/**
 * The shapes on the floor plan, as pure arithmetic.
 *
 * Kept apart from the component so it can be tested without a DOM — the same
 * split as `components/ui/logoGeometry.ts` and `Logo.tsx`, and the only split
 * that works here, because this repo has no jsdom and `vitest.config.ts` matches
 * `*.test.ts` only. A `.test.tsx` would not even run.
 *
 * **The units are arbitrary and never shown.** An earlier draft of this drew the
 * room to scale in metres with walkway warnings; that was explicitly not wanted.
 * What survives is the one thing scale was really for: tables in honest
 * proportion to each other and to the room, so six tables of ten look tighter
 * than six of six. No number from this file is ever rendered.
 */

/** The room. 4:3 because it fits a screen without letterboxing either way. */
export const ROOM = { width: 100, depth: 75 } as const;

/** Positions snap to this, so dragging settles instead of jittering. */
export const SNAP = 0.5;

/** Travel below this many pixels is a tap, not a drag. A fact about fingers. */
export const TAP_SLOP_PX = 6;

export interface Size {
  /** Along the table's own x before rotation. */
  w: number;
  h: number;
  round: boolean;
}

export interface Footprint {
  cx: number;
  cy: number;
  /** Half-extents of the axis-aligned box that contains the table *and* its chairs. */
  ex: number;
  ey: number;
  size: Size;
  deg: number;
}

export interface Seat {
  x: number;
  y: number;
  /** Degrees, pointing away from the table, so a chair can face inward. */
  angle: number;
}

/**
 * How far a chair sits outside the table edge, and how big it is drawn.
 *
 * Both small on purpose. The first draft had chairs 1.5 out and 1.15 across, and
 * they floated in a ring well clear of the table — which reads as decoration
 * rather than as seats. A real chart has them all but touching.
 */
const SEAT_GAP = 0.4;
export const SEAT_R = 0.95;

/**
 * Size from capacity, so nobody types a dimension.
 *
 * Round grows with the **square root** of capacity: people sit around the edge,
 * so the area has to grow with the seats, and a linear diameter would make a
 * ten-top dwarf a six-top rather than merely out-size it.
 */
export function tableSize(shape: TableShape, capacity: number): Size {
  const seats = Math.max(1, capacity);
  if (shape === 'rect') {
    // People sit along both long sides; the ends stay empty.
    return { w: Math.max(7, Math.ceil(seats / 2) * 2.9), h: 4.6, round: false };
  }
  if (shape === 'sweetheart') {
    // A table for two, whatever the capacity field happens to say.
    return { w: 5.6, h: 4, round: false };
  }
  return { w: Math.sqrt(seats) * 3.2, h: Math.sqrt(seats) * 3.2, round: true };
}

/** Rotation is restricted to quarter turns, which keeps every box axis-aligned. */
const quarterTurns = (deg: number): number => Math.round((((deg % 360) + 360) % 360) / 90) % 4;

export function footprint(
  table: Pick<Table, 'shape' | 'capacity' | 'x' | 'y' | 'rotation'>,
): Footprint {
  const size = tableSize(table.shape ?? 'round', table.capacity);
  const turns = quarterTurns(table.rotation ?? 0);
  const swapped = turns % 2 === 1;

  // The chairs are part of the shape as far as the walls are concerned.
  const reach = SEAT_GAP + SEAT_R * 2;
  const w = (swapped ? size.h : size.w) + reach * 2;
  const h = (swapped ? size.w : size.h) + reach * 2;

  return {
    cx: table.x ?? 0,
    cy: table.y ?? 0,
    ex: w / 2,
    ey: h / 2,
    size,
    deg: turns * 90,
  };
}

/**
 * Where every chair goes.
 *
 * The one piece of geometry anybody will actually look at: a glance at the ring
 * tells you table four has two seats spare, which is the whole point of drawing
 * a plan rather than reading a list.
 */
export function seatPositions(table: Pick<Table, 'shape' | 'capacity' | 'rotation'>): Seat[] {
  const shape = table.shape ?? 'round';
  const size = tableSize(shape, table.capacity);
  const n = Math.max(0, table.capacity);
  const deg = quarterTurns(table.rotation ?? 0) * 90;
  const rad = (deg * Math.PI) / 180;
  const spin = ({ x, y }: { x: number; y: number }) => ({
    x: x * Math.cos(rad) - y * Math.sin(rad),
    y: x * Math.sin(rad) + y * Math.cos(rad),
  });

  if (size.round) {
    const r = size.w / 2 + SEAT_GAP + SEAT_R;
    return Array.from({ length: n }, (_, i) => {
      // Start at the top and go clockwise, so seat 1 is where you would expect.
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      return { x: Math.cos(a) * r, y: Math.sin(a) * r, angle: (a * 180) / Math.PI + deg };
    });
  }

  const offsetFor = () => size.h / 2 + SEAT_GAP + SEAT_R;

  if (shape === 'sweetheart') {
    // Both chairs on one side, side by side: the couple sits facing the room,
    // not across the table from each other. Drawing it the other way is the
    // difference between a sweetheart table and a very small dinner table.
    const o = offsetFor();
    return Array.from({ length: Math.min(n, 2) }, (_, i) => {
      const p = spin({ x: (i === 0 ? -1 : 1) * (size.w / 5), y: o });
      return { x: p.x, y: p.y, angle: 90 + deg };
    });
  }

  // Long tables: the larger half along the top edge, the rest along the bottom.
  const top = Math.ceil(n / 2);
  const bottom = n - top;
  const offset = offsetFor();
  const along = (count: number, y: number, angle: number): Seat[] =>
    Array.from({ length: count }, (_, i) => {
      const t = count === 1 ? 0.5 : (i + 0.5) / count;
      const p = spin({ x: (t - 0.5) * size.w, y });
      return { x: p.x, y: p.y, angle: angle + deg };
    });

  return [...along(top, -offset, -90), ...along(bottom, offset, 90)];
}

/** The nearest centre that keeps the whole table, chairs included, in the room. */
export function clampToRoom(f: Footprint, x: number, y: number): { x: number; y: number } {
  // A table wider than the room inverts the range; centre it rather than NaN.
  const clamp = (v: number, half: number, span: number) =>
    half * 2 >= span ? span / 2 : Math.min(Math.max(v, half), span - half);
  return {
    x: clamp(x, f.ex, ROOM.width),
    y: clamp(y, f.ey, ROOM.depth),
  };
}

export const snap = (v: number, step = SNAP): number => Math.round(v / step) * step;

/** Two tables in the same place. Shown, never prevented. */
export function overlaps(a: Footprint, b: Footprint): boolean {
  return Math.abs(a.cx - b.cx) < a.ex + b.ex && Math.abs(a.cy - b.cy) < a.ey + b.ey;
}

/**
 * Whether a table has ever been put anywhere.
 *
 * `x`/`y` default to 0, and 0,0 is also the top-left corner — so the two are
 * indistinguishable in the data. Clamping means a table actually dragged into
 * that corner comes to rest at its own half-extent and never at exactly 0,0, so
 * this holds in practice. It is a convention, which is why it is one named
 * function rather than an `if` in a component.
 */
export const isPlaced = (t: Pick<Table, 'x' | 'y'>): boolean =>
  (t.x ?? 0) !== 0 || (t.y ?? 0) !== 0;

/**
 * A starting arrangement, for tables nobody has placed yet.
 *
 * Without this every table sits at 0,0 in a heap the first time the plan opens,
 * which reads as broken rather than empty. Shelf packing, biggest first, so the
 * result is deterministic — opening the plan twice must not shuffle the room.
 */
export function autoArrange(tables: readonly Table[]): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>();
  const margin = 2;
  const gap = 2;

  const ordered = [...tables].sort((a, b) => {
    const fa = footprint(a);
    const fb = footprint(b);
    return fb.ex * fb.ey - fa.ex * fa.ey || a.tableId.localeCompare(b.tableId);
  });

  let cursorX = margin;
  let cursorY = margin;
  let rowHeight = 0;

  for (const table of ordered) {
    const f = footprint(table);
    if (cursorX + f.ex * 2 > ROOM.width - margin && rowHeight > 0) {
      cursorX = margin;
      cursorY += rowHeight + gap;
      rowHeight = 0;
    }
    const placed = clampToRoom(f, cursorX + f.ex, cursorY + f.ey);
    out.set(table.tableId, { x: snap(placed.x), y: snap(placed.y) });
    cursorX += f.ex * 2 + gap;
    rowHeight = Math.max(rowHeight, f.ey * 2);
  }
  return out;
}

/**
 * A pointer's position in room units.
 *
 * The rect is a parameter rather than read from the DOM, which is what makes
 * this testable and means the drag path calls `getBoundingClientRect` once per
 * gesture rather than once per move event.
 */
export function clientToRoom(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  return {
    x: ((clientX - rect.left) / rect.width) * ROOM.width,
    y: ((clientY - rect.top) / rect.height) * ROOM.depth,
  };
}
